import { Injectable } from "@angular/core";
import { Action, Selector, State, StateContext, StateToken, Store } from "@ngxs/store";
import { CicdRepositoriesService } from "./cicd-repositories.service";
import {
    ClearCicdRepository,
    LoadCicdRepositories,
    LoadCicdRepositoriesCharts,
    LoadCicdRepositoriesChartsFilteredData,
    LoadDataTableCicdRepositories,
    SetCicdRepository,
    UpdateCountersCicdRepositories
} from "./cicd-repositories.actions";
import { tap } from "rxjs";
import { CicdRepositoriesStateModel, CicdRepository } from "./cicd-repositories.model";
import { PreferencesFiltersState } from "src/app/store/preferences/filters/preferences-filters.state";
import { CompliantRatioService } from "src/app/shared/service/compliant-ratio.service";
import { ChartComplianceRatioItem } from "src/app/shared/component/chart-compliance-ratio/chart-compliance-ratio.component";
import { CicdRepositoryTypeEnum } from "src/app/shared/enum/cicd-repository-type.enum";
import { DateService } from "src/app/shared/service/date.service";
import { ChartComplianceTotalItem } from "src/app/shared/component/chart-compliance-total/chart-compliance-total.component";
import { ChartVersusItem } from "src/app/shared/component/chart-versus/chart-versus.component";
import { CicdRepositoryTypeLabelPipe } from "src/app/shared/pipe/cicd-repository-type-label.pipe";
import { ColorEnum } from "src/app/shared/enum/color.enum";
import { ItemTypeEnum } from "src/app/shared/enum/item-type.enum";
import { MetaService } from "src/app/store/meta/meta.service";
import { MetaRequestHelper } from "src/app/store/meta/meta-request.helper";
import { PreferencesChartsState } from "src/app/store/preferences/charts/preferences-charts.state";

export const CICD_TOKEN: StateToken<CicdRepositoriesStateModel> = new StateToken('cicdRepositories');

const CicdRepositoriesStateModelDefault: CicdRepositoriesStateModel = {
    repositories: [],
    compliantRatio: '0%',
    totalItems: '0',
    compliantItems: '0',
    notCompliantItems: '0',
    datatable: [],
    selectedRepository: {
        age: 0,
        id: '',
        issues: [],
        name: '',
        state: 'ignored',
        branches: [],
        challengers: [],
        archived: false,
        type: '',
        url: '',
        full_path:'',
        statistics: {
            "active_branches_count": "",
            "commit_count": "",
            "wiki_size": "",
            "packages_size": "",
            "inactive_branches_count": "",
            "lfs_objects_size": "",
            "job_artifacts_size": "",
            "repository_size": "",
            "storage_size": "",
            "uploads_size": "",
            "snippets_size": "",
            "pipeline_artifacts_size": ""
        },
        "technology_stacks": [],
        "cicd-docker-images": [],
        "cicd-libraries": [],
        "cicd-templates": []
    },
    chartsData: {
        measures: {}
    },
    filteredComplianceRatioChartData: {
        values: []
    },
    filteredComplianceTotalChartData: {
        values: []
    },
    filteredVersusChartData: {
        values: []
    }
}


@State<CicdRepositoriesStateModel>({
    name: CICD_TOKEN,
    defaults: CicdRepositoriesStateModelDefault
})
@Injectable()
export class CicdRepositoriesState {

    private readonly KEY = CICD_TOKEN.getName()

    constructor(
        private cicdRepositoriesService: CicdRepositoriesService,
        private store: Store,
        private dateService: DateService,
        private cicdRepositoryTypeLabelPipe: CicdRepositoryTypeLabelPipe,
        private metaService: MetaService
    ) {
    }

    @Selector([CICD_TOKEN])
    static getRepositories(state: CicdRepositoriesStateModel) {
        return state.repositories
    }

    @Selector([CICD_TOKEN])
    static getCompliantRatio(state: CicdRepositoriesStateModel) {
        return state.compliantRatio
    }

    @Selector([CICD_TOKEN])
    static getTotalItems(state: CicdRepositoriesStateModel) {
        return state.totalItems
    }

    @Selector([CICD_TOKEN])
    static getCompliantItems(state: CicdRepositoriesStateModel) {
        return state.compliantItems
    }

    @Selector([CICD_TOKEN])
    static getNotCompliantItems(state: CicdRepositoriesStateModel) {
        return state.notCompliantItems
    }

    @Selector([CICD_TOKEN])
    static getDatatable(state: CicdRepositoriesStateModel) {
        return state.datatable
    }

    @Selector([CICD_TOKEN])
    static getSelectedRepository(state: CicdRepositoriesStateModel) {
        return state.selectedRepository
    }

    @Selector([CICD_TOKEN])
    static getFilteredComplianceRatioChartData(state: CicdRepositoriesStateModel) {
        return state.filteredComplianceRatioChartData
    }

    @Selector([CICD_TOKEN])
    static getFilteredComplianceTotalChartData(state: CicdRepositoriesStateModel) {
        return state.filteredComplianceTotalChartData
    }

    @Selector([CICD_TOKEN])
    static getFilteredVersusChartData(state: CicdRepositoriesStateModel) {
        return state.filteredVersusChartData
    }

    @Action(LoadCicdRepositories)
    loadCicdRepositories(ctx: StateContext<CicdRepositoriesStateModel>) {
        const REQUEST_KEY = this.KEY + "-" + LoadCicdRepositories.name

        if(!this.metaService.isStale(REQUEST_KEY)) return

        return MetaRequestHelper.wrapRequest(
            this.store,
            REQUEST_KEY,
            this.cicdRepositoriesService.loadCicdGitlabRepositories(),
            repositories => {
                ctx.patchState(
                    {
                        repositories: repositories
                    }
                )
                ctx.dispatch(new LoadDataTableCicdRepositories())
            }
        )
    }

    @Action(LoadDataTableCicdRepositories)
    loadDataTableCicdRepositories(ctx: StateContext<CicdRepositoriesStateModel>) {
        const state = ctx.getState()
        let datatable = state.repositories

        let filters = this.store.selectSnapshot(PreferencesFiltersState.getFiltersByType)[ItemTypeEnum[ItemTypeEnum.CICD_REPOSITORIES]]
        filters.forEach(filter => {
            if (filter.key == 'technologyStack') {
                datatable = datatable.filter((repository) => {
                    let technologyStackFound = false
                    repository.technology_stacks.forEach((technologyStack: string) => {
                        technologyStackFound = technologyStackFound || filter.value.includes(technologyStack)
                    })
                    return technologyStackFound
                })
            } else if (filter.key == 'issuesCount') {
                datatable = datatable.filter((repository) => repository['issues'].length.toString() === filter.value)
            } else if (filter.key == 'issuesType') {
                datatable = datatable.filter((repository) => {
                    let issuesTypeFound = false
                    repository.issues.forEach(issue => {
                        issuesTypeFound = issuesTypeFound || filter.value.includes(issue.issue_type)
                    })
                    return issuesTypeFound
                })
            } else if (filter.key == 'age') {
                let unknownAge = 99999
                let filterAge: number = unknownAge
                if (filter.value == 'today') {
                    filterAge = 0
                } else if (filter.value.startsWith('last_')) {
                    filterAge = filter.value.substring(5, 7)
                }
                datatable = datatable.filter((repository) => {
                    let ageInRange = false
                    if (filterAge == unknownAge) {
                        ageInRange = repository.age == filterAge
                    } else {
                        ageInRange = repository.age <= filterAge
                    }
                    return ageInRange
                })
            } else if (filter.key == 'challenger') {
                datatable = datatable.filter((repository) => {
                    let challengerFound = false
                    repository.challengers.forEach(challenger => {
                        challengerFound = challengerFound || challenger == filter.value
                    })
                    return challengerFound
                })
            } else {
                if (Array.isArray(filter.value)) {
                    datatable = datatable.filter((repository) => filter.value.includes(repository[filter.key as keyof CicdRepository]))
                } else {
                    datatable = datatable.filter((repository) => repository[filter.key as keyof CicdRepository] === filter.value)
                }
            }
        });
        ctx.patchState({
            datatable: datatable
        })
        ctx.dispatch(new UpdateCountersCicdRepositories())
    }

    @Action(UpdateCountersCicdRepositories)
    updateCountersCicdRepositories(ctx: StateContext<CicdRepositoriesStateModel>) {
        const state = ctx.getState()
        let totalItemsCounter = 0
        let compliantItemsCounter = 0
        let notCompliantItemsCounter = 0
        state.datatable.forEach(repository => {
            if (repository.state == 'compliant') {
                totalItemsCounter++;
                compliantItemsCounter++;
            } else if (repository.state == 'not_compliant') {
                totalItemsCounter++;
                notCompliantItemsCounter++;
            }
        });
        ctx.patchState({
            compliantRatio: CompliantRatioService.calculateCompliantRatio(compliantItemsCounter, notCompliantItemsCounter),
            totalItems: '' + totalItemsCounter,
            compliantItems: '' + compliantItemsCounter,
            notCompliantItems: '' + notCompliantItemsCounter
        })
    }

    @Action(ClearCicdRepository)
    clearCicdRepository(ctx: StateContext<CicdRepositoriesStateModel>) {
        return ctx.patchState({
            selectedRepository: CicdRepositoriesStateModelDefault.selectedRepository
        })
    }

    @Action(SetCicdRepository)
    setCicdRepository(ctx: StateContext<CicdRepositoriesStateModel>, {cicd_repository_id}: SetCicdRepository) {
        return this.cicdRepositoriesService.loadCicdGitlabRepositoryDetails(cicd_repository_id)
            .pipe(
                tap(value => {
                    return ctx.patchState({
                        selectedRepository: value
                    })
                }),
            )
    }

    @Action(LoadCicdRepositoriesCharts)
    loadCicdRepositoriesCharts(ctx: StateContext<CicdRepositoriesStateModel>) {
        const chartsConfig = this.store.selectSnapshot(PreferencesChartsState.getConfig);
        const { start_date, end_date } = DateService.getChartsMeasuresDateRange(chartsConfig);
        const REQUEST_KEY = this.KEY + "-" + LoadCicdRepositoriesCharts.name + "-" + start_date + "-" + end_date + "-" + chartsConfig.granularity
        if(!this.metaService.isStale(REQUEST_KEY)) return

        return MetaRequestHelper.wrapRequest(
            this.store,
            REQUEST_KEY,
            this.cicdRepositoriesService.loadCicdGitlabRepositoriesMeasures(start_date, end_date, chartsConfig.granularity),
            charts_payload => {
                ctx.patchState(
                    {
                        chartsData: charts_payload
                    }
                )
                ctx.dispatch(new LoadCicdRepositoriesChartsFilteredData())
            }
        )
    }

    @Action(LoadCicdRepositoriesChartsFilteredData)
    loadCicdRepositoriesChartsFilteredData(ctx: StateContext<CicdRepositoriesStateModel>) {
        let chartsData = ctx.getState().chartsData
        let complianceRatioChartValues: ChartComplianceRatioItem[] = [];
        let complianceTotalChartValues: ChartComplianceTotalItem[] = [];
        let complianceVersusChartValues: ChartVersusItem[] = [];
        let filters = this.store.selectSnapshot(PreferencesFiltersState.getFiltersByType)[ItemTypeEnum[ItemTypeEnum.CICD_MEASURES]]
        let types: string[] = []
        filters.forEach(filter => {
            types = filter.value
        })
        if (types.length == 0) {
            types = Object.keys(CicdRepositoryTypeEnum)
        }
        for (const dateString in chartsData.measures) {
            const dateMeasures = chartsData.measures[dateString];
            let formattedDate = this.dateService.formatLongDate(dateString)
            let compliantCount = 0
            let notCompliantCount = 0
            for (const type in dateMeasures) {
                if (type != CicdRepositoryTypeEnum[CicdRepositoryTypeEnum.training] && types.includes(type)) {
                    const typeMeasures = dateMeasures[type];
                    compliantCount += typeMeasures.compliant;
                    notCompliantCount += typeMeasures.not_compliant;
                }
            }
            complianceRatioChartValues.push({
                "dateString": formattedDate,
                "date": new Date(formattedDate),
                "value": CompliantRatioService.calculateCompliantRatio(compliantCount, notCompliantCount)
            });
            complianceTotalChartValues.push({
                "dateString": formattedDate,
                "date": new Date(formattedDate),
                "compliantCount": compliantCount,
                "notCompliantCount": notCompliantCount,
                "total": compliantCount + notCompliantCount,
                "delta": Math.abs(compliantCount - notCompliantCount)
            });
        }
        let lastDate = Object.keys(chartsData.measures)[Object.keys(chartsData.measures).length - 1]
        let lastMeasure = chartsData.measures[lastDate]
        for (const type in lastMeasure) {
            if (type != CicdRepositoryTypeEnum[CicdRepositoryTypeEnum.training] && types.includes(type)) {
                let typeMeasure = lastMeasure[type]
                let colorKey = 'charts_color_' + complianceVersusChartValues.length
                let dateData = {
                    "type": this.cicdRepositoryTypeLabelPipe.transform(type),
                    "count": typeMeasure.compliant + typeMeasure.not_compliant + typeMeasure.ignored,
                    "color": ColorEnum[colorKey as keyof typeof ColorEnum]
                };
                complianceVersusChartValues.push(dateData);
            }
        }
        return ctx.patchState({
            filteredComplianceRatioChartData: {
                values: complianceRatioChartValues
            },
            filteredComplianceTotalChartData: {
                values: complianceTotalChartValues
            },
            filteredVersusChartData: {
                values: complianceVersusChartValues
            }
        })
    }
}
